import React, { FormEvent, FormEventHandler, useState } from "react";
import { useMediaQuery } from "react-responsive";
import filterData from "../services/filterData";
import { Box } from "../styles/Box";
import { Button } from "../styles/Button";
import { Heading } from "../styles/Heading";
import { Input } from "../styles/Input";
import { Message } from "../styles/Message";
import { Select } from "../styles/Select";
import { colorScales, primaryColorScales } from "../theme/styles";
import { PensionCalculatorResponse } from "../types";
import Table from "./Table";

type TForm = {
  pensionContract: string;
  monthlySalary: string;
};

type TTable = {
  averageMonthlySalary: number;
  yearlySalary: number;
  selectInput: string;
};

const numbers = /^[0-9]*$/;

type Props = { data: PensionCalculatorResponse[] };

const PensionCalculator: React.FC<Props> = (props) => {
  const collection = filterData(props);

  const isMobile = useMediaQuery({
    query: "(max-width: 599px)",
  });
  const isTablet = useMediaQuery({
    query: "(min-width: 600px) and (max-width: 899px)",
  });
  const isDesktop = useMediaQuery({
    query: "(min-width: 900px)",
  });

  const [formState, setFormState] = useState<TForm>({
    pensionContract: "",
    monthlySalary: "",
  });

  const [validatedInput, setValidatedInput] = useState<{
    pensionContractValidated: boolean;
    monthlySalary: boolean;
  }>({
    pensionContractValidated: true,
    monthlySalary: false,
  });

  const [showTable, setShowTable] = useState<boolean>(false);

  const [tableData, settableData] = useState<TTable>({
    averageMonthlySalary: 0,
    yearlySalary: 0,
    selectInput: "",
  });

  const selectOptionStyle = {
    fontSize: "1.125rem",
    color: colorScales.blueScale[3],
  };

  const validateInput = () => {
    return (
      validatedInput.monthlySalary &&
      validatedInput.pensionContractValidated &&
      formState.monthlySalary.length > 0
    );
  };

  const handleSalaryInput = (
    event: React.ChangeEvent<HTMLInputElement> | undefined,
  ) => {
    const salaryInput = event as React.ChangeEvent<HTMLInputElement>;

    setFormState((prevFormState) => {
      return {
        ...prevFormState,
        monthlySalary: salaryInput.target.value,
      };
    });

    setValidatedInput((prevValidatedInput) => {
      return {
        ...prevValidatedInput,
        monthlySalary: numbers.test(salaryInput.target.value),
      };
    });
  };

  const handlePensionContractInput: FormEventHandler<HTMLDivElement> = (
    event,
  ) => {
    const pensionContractInput = (event.target as HTMLSelectElement).value;
    setFormState((prevFormstate) => {
      return {
        ...prevFormstate,
        pensionContract: pensionContractInput,
      };
    });
    setValidatedInput((prevValidatedInput) => {
      return {
        ...prevValidatedInput,
        pensionContractValidated: true,
      };
    });
  };

  const checkPensionContract = () => {
    if (!formState.pensionContract) {
      setValidatedInput((prevValidatedInput) => {
        return {
          ...prevValidatedInput,
          pensionContractValidated: false,
        };
      });
    }
  };

  const submitForm = (event: React.FormEvent<HTMLDivElement> | undefined) => {
    const e = event as React.FormEvent<HTMLDivElement>;
    e.preventDefault();
  };

  const updateTable = () => {
    settableData((prevTableData) => {
      return {
        ...prevTableData,
        averageMonthlySalary: parseInt(formState.monthlySalary),
        yearlySalary: parseInt(formState.monthlySalary) * 12,
        selectInput: formState.pensionContract,
      };
    });
    setShowTable(true);
  };

  return (
    <Box
      as="form"
      onSubmit={submitForm}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
    >
      <Heading
        variant="h2"
        as="h2"
        sx={{
          fontSize: isTablet || isDesktop ? "32px" : "22px",
          marginBottom: "12px",
        }}
      >
        {collection.header}
      </Heading>
      <Heading
        as="label"
        htmlFor="pensionContract"
        sx={{
          fontSize: "18px",
          marginBottom: "12px",
          width: "150px",
        }}
      >
        {collection.selectLabel}
      </Heading>
      <Box as="div" sx={{ marginBottom: "22px" }}>
        <Select
          onChange={handlePensionContractInput}
          name={"pensionContract"}
          id={"pensionContract"}
          sx={{
            width: "100%",
            fontSize: "1.125rem",
            color: colorScales.blueScale[3],
          }}
        >
          <option disabled selected hidden>
            Välj ditt avtal
          </option>
          <option style={selectOptionStyle} value="Privatanställda arbetare">
            Privatanställda arbetare
          </option>
          <option
            style={selectOptionStyle}
            value="Anställda inom kooperationen GTP"
          >
            Anställda inom kooperationen GTP
          </option>
          <option
            style={selectOptionStyle}
            value={"Anställda inom kooperationen KTP1 AFO-Handels"}
          >
            Anställda inom kooperationen KTP1 AFO-Handels
          </option>
          <option style={selectOptionStyle} value="Kommun och region AKAP-KR">
            Kommun och region AKAP-KR
          </option>
          <option style={selectOptionStyle} value="Kommun och region KAP-KL">
            Kommun och region KAP-KL
          </option>
        </Select>
      </Box>
      {!validatedInput.pensionContractValidated ? (
        <Message
          variant="warning"
          sx={{
            marginBottom: "28px",
          }}
        >
          {collection.inputLabelError}
        </Message>
      ) : null}

      <Heading
        as="label"
        htmlFor="monthlySalary"
        sx={{ fontSize: "18px", marginBottom: "12px" }}
      >
        {collection.inputLabel}
      </Heading>
      <Input
        maxLength={10}
        type="text"
        variant="input"
        value={formState.monthlySalary}
        name="monthlySalary"
        id="monthlySalary"
        onChange={handleSalaryInput}
        onFocus={checkPensionContract}
        sx={{
          marginBottom:
            !validatedInput.monthlySalary && formState.monthlySalary.length > 0
              ? "22px"
              : "28px",
        }}
      />
      {!validatedInput.monthlySalary && formState.monthlySalary.length > 0 ? (
        <Message variant="warning" sx={{ marginBottom: "28px" }}>
          {collection.inputLabelError} i siffror
        </Message>
      ) : null}
      <Button
        sx={{
          width: isMobile ? "100%" : isTablet ? "7rem" : "7rem",
          opacity: validateInput() ? "1" : "0.5",
          boxShadow: `inset 0rem -0.125rem 0rem 0rem ${primaryColorScales.blueScale[5]}`,
          marginBottom: isDesktop ? "28px" : "1.5rem",
          ":hover": {
            opacity: validateInput() ? "0.8" : "0.5",
            cursor: validateInput() ? "pointer" : "inherit",
          },
        }}
        disabled={!validateInput()}
        onClick={() => updateTable()}
      >
        {collection.buttonCalculate}
      </Button>
      <Box
        as="div"
        sx={{
          borderBottom: "0.0625rem solid #ebebeb",
        }}
      >
        {""}
      </Box>
      {showTable ? (
        <Table
          data={props.data}
          selectInput={tableData.selectInput}
          averageMonthlySalary={tableData.averageMonthlySalary}
          yearlySalary={tableData.yearlySalary}
        />
      ) : null}
    </Box>
  );
};

export default PensionCalculator;
